import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_toString() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🧩 JavaScript toString() Method</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>toString()</b> method in JavaScript converts a value or object to
        a string representation. Almost all JavaScript data types have a{" "}
        <code>toString()</code> method.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Converts numbers, arrays, and objects to strings.</li>
          <li>
            Can be used directly on variables or implicitly when needed in a
            string context.
          </li>
          <li>
            Every JavaScript object inherits the <code>toString()</code> method.
          </li>
          <li>
            Custom objects can override <code>toString()</code> for custom
            behavior.
          </li>
        </ul>
      </div>

      {/* Example 1: Number to String */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Number to String</h2>
        <p>
          The <code>toString()</code> method converts numbers into string
          representations.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let num = 123;
console.log(num.toString());
console.log(typeof num.toString());`}
          />
          <TryItButton
            code={`let num = 123;
console.log(num.toString());
console.log(typeof num.toString());`}
          />
        </div>

        <OutputBox>
          <pre>
            123
            {"\n"}string
          </pre>
        </OutputBox>
      </div>

      {/* Example 2: Boolean to String */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Boolean to String</h2>
        <p>
          Booleans also have a <code>toString()</code> method that returns
          either <code>"true"</code> or <code>"false"</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let flag = true;
console.log(flag.toString());
console.log(typeof flag.toString());`}
          />
          <TryItButton
            code={`let flag = true;
console.log(flag.toString());
console.log(typeof flag.toString());`}
          />
        </div>

        <OutputBox>
          <pre>
            true
            {"\n"}string
          </pre>
        </OutputBox>
      </div>

      {/* Example 3: Array to String */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Array to String</h2>
        <p>
          Arrays use <code>toString()</code> to join all elements with commas.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["apple", "banana", "cherry"];
console.log(fruits.toString());`}
          />
          <TryItButton
            code={`let fruits = ["apple", "banana", "cherry"];
console.log(fruits.toString());`}
          />
        </div>

        <OutputBox>
          <pre>apple,banana,cherry</pre>
        </OutputBox>
      </div>

      {/* Example 4: Object to String */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Object to String</h2>
        <p>
          By default, <code>toString()</code> for objects returns{" "}
          <code>"[object Object]"</code>. You can override this behavior.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const person = {
  name: "Alice",
  age: 25,
  toString: function() {
    return this.name + " (" + this.age + ")";
  }
};
console.log(person.toString());`}
          />
          <TryItButton
            code={`const person = {
  name: "Alice",
  age: 25,
  toString: function() {
    return this.name + " (" + this.age + ")";
  }
};
console.log(person.toString());`}
          />
        </div>

        <OutputBox>
          <pre>Alice (25)</pre>
        </OutputBox>
      </div>

      {/* Example 5: Implicit Conversion */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Implicit Conversion</h2>
        <p>
          When JavaScript expects a string (for example, during concatenation),
          it automatically calls <code>toString()</code> on the value.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let num = 100;
let text = "Value: " + num; // JS calls num.toString() automatically
console.log(text);`}
          />
          <TryItButton
            code={`let num = 100;
let text = "Value: " + num;
console.log(text);`}
          />
        </div>

        <OutputBox>
          <pre>Value: 100</pre>
        </OutputBox>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>toString()</code> when converting non-string values to
            text.
          </li>
          <li>
            Avoid calling <code>toString()</code> on <code>null</code> or{" "}
            <code>undefined</code> — it will cause errors.
          </li>
          <li>
            Override <code>toString()</code> in custom objects to control how
            they display.
          </li>
          <li>
            Remember: Arrays automatically join elements using commas when
            converted to strings.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>String Methods</b> like <code>slice()</code> and{" "}
            <code>substring()</code>.
          </li>
          <li>
            Explore <b>Number Methods</b> like <code>toFixed()</code> and{" "}
            <code>toPrecision()</code>.
          </li>
          <li>
            Understand <b>Type Conversion</b> in JavaScript to convert between
            types safely.
          </li>
        </ul>
      </div>
    </div>
  );
}
